import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Chip, Stack, Skeleton } from "@mui/material";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveIcon from "@mui/icons-material/Remove";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { useGetProgressQuery, useGetSummaryQuery } from "@store/gameApi";
import type { GameSummaryResponse } from "@models/game";
import DashboardTile from "@components/Home/DashboardTile/DashboardTile";
import DomainRadarMiniChart from "../GamesCTATile/DomainRadarMiniChart";
import WeeklyActivityBar from "../GamesCTATile/WeeklyActivityBar";

const DOMAIN_KEYS = [
  "memory",
  "attention",
  "processing_speed",
  "language",
  "cognitive_flexibility",
  "spatial",
] as const;

export interface CaregiverGamesPanelProps {
  patientId: string;
}

const CaregiverGamesPanel: FC<CaregiverGamesPanelProps> = ({ patientId }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetSummaryQuery({ patientId });
  const { data: progressData } = useGetProgressQuery(
    { patientId },
    { skip: !patientId },
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <PsychologyOutlinedIcon color="primary" />
          <Typography variant="h6">{t("games.caregiverPanel.title")}</Typography>
        </Stack>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </Box>
    );
  }

  if (isError || !data) {
    return null;
  }

  const summary = data as GameSummaryResponse;
  const hasDeltas = summary.domainDeltas && Object.keys(summary.domainDeltas).length > 0;
  const failSafe = summary.failSafeTriggersLast30Days ?? 0;
  const alerts = summary.alerts ?? [];
  const lastActive = summary.lastActiveAt;
  const streak = summary.currentStreak ?? 0;

  return (
    <DashboardTile title={t("games.caregiverPanel.title")}>

      {lastActive && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("games.caregiverPanel.lastActive", {
            date: new Date(lastActive).toLocaleDateString(),
          })}
        </Typography>
      )}

      {streak > 0 && (
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
          <LocalFireDepartmentIcon sx={{ color: "warning.main", fontSize: 20 }} />
          <Typography variant="body2">
            {t("games.caregiverPanel.streak", { count: streak })}
          </Typography>
        </Stack>
      )}

      {hasDeltas && summary.domainDeltas && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {t("games.caregiverPanel.domainChange")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {DOMAIN_KEYS.map((key) => {
              const delta = summary.domainDeltas![key];
              if (delta == null) return null;
              const label = t(`games.sessionSummary.domains.${key}`);
              return (
                <Chip
                  key={key}
                  size="small"
                  icon={
                    delta > 0 ? (
                      <TrendingUpIcon sx={{ fontSize: 14 }} />
                    ) : delta < 0 ? (
                      <TrendingDownIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <RemoveIcon sx={{ fontSize: 14 }} />
                    )
                  }
                  label={`${label} ${delta > 0 ? "+" : ""}${delta}`}
                  color={delta > 0 ? "success" : delta < 0 ? "warning" : "default"}
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary">
        {failSafe === 0
          ? t("games.caregiverPanel.difficultyStable")
          : t("games.caregiverPanel.difficultyAdjusted", { count: failSafe })}
      </Typography>

      <DomainRadarMiniChart progress={progressData ?? null} />
      <WeeklyActivityBar progress={progressData ?? null} />

      {alerts.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {alerts.map((a, i) => (
            <Typography key={i} variant="caption" color="warning.main">
              {a.label}
            </Typography>
          ))}
        </Stack>
      )}
    </DashboardTile>
  );
};

export default CaregiverGamesPanel;
